import { c as _c } from "react/compiler-runtime";
// Просмотр файла задания: фото, PDF, таблица (с подсвеченной строкой
// источника), текст. Байты — из приватного бакета через шлюз, поэтому
// objectURL, как у документов продавца.
import { Download, ExternalLink, Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { readSpreadsheet, type Spreadsheet } from "@osmix/catalog-core/spreadsheet";
import type { CatalogImportFile } from "@osmix/contracts";
import { useImportFileUrl } from "@/api/hooks/use-catalog-import";
import { Button } from "@osmix/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@osmix/ui/dialog";
import { cn } from "@/lib/utils";
import { FILE_KIND_LABELS, errorText, formatBytes } from "./shared";
const TEXT_LIMIT = 200_000;
const ROW_LIMIT = 400;

/** «лист Товары, строка 12» → 12; «стр. 3» → страница 3. */
const rowOf = (locator?: string) => Number(locator?.match(/строк[аи]\s*(\d+)/i)?.[1] ?? 0) || null;
const pageOf = (locator?: string) => Number(locator?.match(/стр\.?\s*(\d+)/i)?.[1] ?? 0) || null;
export function ImportFileViewer(t0) {
  const $ = _c(49);
  const {
    jobId,
    file,
    locator,
    onClose
  } = t0;
  const {
    url,
    blob,
    error,
    isLoading
  } = useImportFileUrl(jobId, file?.id, !!file);
  const kind = file?.kind;
  let t1;
  if ($[0] !== blob || $[1] !== kind) {
    t1 = kind === "text" || !!blob && blob.type.startsWith("text/") && kind !== "spreadsheet";
    $[0] = blob;
    $[1] = kind;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const isText = t1;
  let t2;
  if ($[3] !== locator) {
    t2 = pageOf(locator);
    $[3] = locator;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const page = t2;
  let t3;
  if ($[5] !== file || $[6] !== url) {
    t3 = () => {
      if (!url || !file) {
        return;
      }
      const a = document.createElement("a");
      a.href = url;
      a.download = file.filename;
      a.click();
    };
    $[5] = file;
    $[6] = url;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const download = t3;
  const t4 = !!file;
  let t5;
  if ($[8] !== onClose) {
    t5 = open => !open && onClose();
    $[8] = onClose;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const t6 = file?.path || file?.filename;
  let t7;
  if ($[10] !== t6) {
    t7 = <DialogTitle className="truncate">{t6}</DialogTitle>;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const t8 = file ? `${FILE_KIND_LABELS[file.kind]} · ${formatBytes(file.sizeBytes)}` : "";
  const t9 = locator ? ` · источник позиции: ${locator}` : "";
  let t10;
  if ($[12] !== t8 || $[13] !== t9) {
    t10 = <DialogDescription>{t8}{t9}</DialogDescription>;
    $[12] = t8;
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== t10 || $[16] !== t7) {
    t11 = <DialogHeader className="pr-8">{t7}{t10}</DialogHeader>;
    $[15] = t10;
    $[16] = t7;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== blob || $[19] !== error || $[20] !== file?.filename || $[21] !== isLoading || $[22] !== isText || $[23] !== kind || $[24] !== locator || $[25] !== page || $[26] !== url) {
    t12 = <div className="flex min-h-[40vh] flex-1 items-center justify-center overflow-auto rounded-md border bg-muted/30">{error ? <p className="p-6 text-sm text-destructive">Файл не открылся: {errorText(error)}</p> : isLoading || !blob ? <span className="inline-flex items-center gap-2 p-6 text-sm text-muted-foreground"><Loader2 className="size-4 animate-spin" />Загружаем файл…</span> : kind === "image" && url ? <img src={url} alt={file?.filename ?? ""} className="max-h-[70vh] w-auto object-contain" /> : kind === "pdf" && url ? <iframe src={page ? `${url}#page=${page}` : url} title={file?.filename} className="h-[70vh] w-full" /> : kind === "spreadsheet" ? <SheetPreview blob={blob} filename={file?.filename ?? ""} row={rowOf(locator)} /> : isText ? <TextPreview blob={blob} /> : <p className="p-6 text-center text-sm text-muted-foreground">Предпросмотр для этого формата недоступен — скачайте файл.</p>}</div>;
    $[18] = blob;
    $[19] = error;
    $[20] = file?.filename;
    $[21] = isLoading;
    $[22] = isText;
    $[23] = kind;
    $[24] = locator;
    $[25] = page;
    $[26] = url;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  const t13 = !url;
  let t14;
  if ($[28] !== url) {
    t14 = () => url && window.open(url, "_blank", "noopener");
    $[28] = url;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <ExternalLink className="size-4" />;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== t13 || $[32] !== t14) {
    t16 = <Button variant="outline" disabled={t13} onClick={t14}>{t15}Открыть в новой вкладке</Button>;
    $[31] = t13;
    $[32] = t14;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  const t17 = !url;
  let t18;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <Download className="size-4" />;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== download || $[36] !== t17) {
    t19 = <Button disabled={t17} onClick={download}>{t18}Скачать</Button>;
    $[35] = download;
    $[36] = t17;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  let t20;
  if ($[38] !== t16 || $[39] !== t19) {
    t20 = <div className="flex flex-wrap justify-end gap-2">{t16}{t19}</div>;
    $[38] = t16;
    $[39] = t19;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  let t21;
  if ($[41] !== t11 || $[42] !== t12 || $[43] !== t20) {
    t21 = <DialogContent className="flex max-h-[90dvh] flex-col gap-3 sm:max-w-4xl">{t11}{t12}{t20}</DialogContent>;
    $[41] = t11;
    $[42] = t12;
    $[43] = t20;
    $[44] = t21;
  } else {
    t21 = $[44];
  }
  let t22;
  if ($[45] !== t21 || $[46] !== t4 || $[47] !== t5) {
    t22 = <Dialog open={t4} onOpenChange={t5}>{t21}</Dialog>;
    $[45] = t21;
    $[46] = t4;
    $[47] = t5;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  return t22;
}
function TextPreview(t0) {
  const $ = _c(7);
  const {
    blob
  } = t0;
  const [text, setText] = useState(null);
  let t1;
  let t2;
  if ($[0] !== blob) {
    t1 = () => {
      let alive = true;
      blob.slice(0, TEXT_LIMIT).text().then(t => alive && setText(t));
      return () => {
        alive = false;
      };
    };
    t2 = [blob];
    $[0] = blob;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  if (text == null) {
    let t3;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <Loader2 className="size-4 animate-spin text-muted-foreground" />;
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    return t3;
  }
  const t3 = blob.size > TEXT_LIMIT ? "\n\u2026" : "";
  let t4;
  if ($[4] !== t3 || $[5] !== text) {
    t4 = <pre className="h-full max-h-[70vh] w-full self-stretch overflow-auto whitespace-pre-wrap break-words p-4 text-left font-mono text-xs">{text}{t3}</pre>;
    $[4] = t3;
    $[5] = text;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}
function SheetPreview(t0) {
  const $ = _c(27);
  const {
    blob,
    filename,
    row
  } = t0;
  const [sheet, setSheet] = useState(null);
  const [error, setError] = useState(null);
  const highlight = useRef(null);
  let t1;
  let t2;
  if ($[0] !== blob || $[1] !== filename) {
    t1 = () => {
      let alive = true;
      setSheet(null);
      setError(null);
      blob.arrayBuffer().then(buf => readSpreadsheet(new Uint8Array(buf), filename)).then(s => alive && setSheet(s)).catch(e => alive && setError(errorText(e)));
      return () => {
        alive = false;
      };
    };
    t2 = [blob, filename];
    $[0] = blob;
    $[1] = filename;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[4] !== row || $[5] !== sheet) {
    t3 = () => {
      if (sheet && row) {
        highlight.current?.scrollIntoView({
          block: "center"
        });
      }
    };
    t4 = [sheet, row];
    $[4] = row;
    $[5] = sheet;
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  useEffect(t3, t4);
  if (error) {
    let t5;
    if ($[8] !== error) {
      t5 = <p className="p-6 text-sm text-muted-foreground">Таблица не читается в браузере: {error}</p>;
      $[8] = error;
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    return t5;
  }
  if (!sheet) {
    let t5;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <Loader2 className="size-4 animate-spin text-muted-foreground" />;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    return t5;
  }
  let t5;
  let t6;
  let t7;
  if ($[11] !== row || $[12] !== sheet.rows) {
    const rows = sheet.rows.slice(0, ROW_LIMIT);
    t7 = "h-full max-h-[70vh] w-full self-stretch overflow-auto";
    t6 = "w-full border-collapse text-xs";
    t5 = rows.map(r => <tr key={r.n} ref={r.n === row ? highlight : undefined} className={cn("border-b", r.n === row && "bg-amber-500/15 font-medium")}><td className="sticky left-0 bg-muted px-2 py-1 text-right tabular-nums text-muted-foreground">{r.n}</td>{r.cells.map(_temp)}</tr>);
    $[11] = row;
    $[12] = sheet.rows;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    t5 = $[13];
    t6 = $[14];
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t5) {
    t8 = <tbody>{t5}</tbody>;
    $[16] = t5;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t6 || $[19] !== t8) {
    t9 = <table className={t6}>{t8}</table>;
    $[18] = t6;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== sheet.rows.length) {
    t10 = sheet.rows.length > ROW_LIMIT && <p className="p-2 text-xs text-muted-foreground">Показаны первые {ROW_LIMIT} строк из {sheet.rows.length}.</p>;
    $[21] = sheet.rows.length;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t10 || $[24] !== t7 || $[25] !== t9) {
    t11 = <div className={t7}>{t9}{t10}</div>;
    $[23] = t10;
    $[24] = t7;
    $[25] = t9;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  return t11;
}
function _temp(cell, i) {
  return <td key={i} className="max-w-64 truncate whitespace-nowrap px-2 py-1" title={cell}>{cell}</td>;
}